// ChangePasswordForm.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './ChangePasswordForm.scss';

/**
 * Password change form component with validation
 */
function ChangePasswordForm({ onChangePassword, onCancel }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    match: true,
    requirements: true
  });
  const [showRequirements, setShowRequirements] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });
    
    // Clear errors when user types
    if (id === 'newPassword' || id === 'confirmPassword') {
      setErrors({
        ...errors,
        match: true
      });
    }
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}/;
    return passwordRegex.test(password);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const { currentPassword, newPassword, confirmPassword } = formData;
    const passwordsMatch = newPassword === confirmPassword;
    const meetsRequirements = validatePassword(newPassword);
    
    if (passwordsMatch && meetsRequirements) {
      onChangePassword(currentPassword, newPassword);
    } else {
      setErrors({
        match: passwordsMatch,
        requirements: meetsRequirements
      });
    }
  };

  return (
    <div className="password-form">
      <div className="password-form__header">
        <h2>{t('Change Your Password')}</h2>
      </div>
      
      <form onSubmit={handleFormSubmit} className="password-form__content">
        <div className="password-form__field">
          <label htmlFor="currentPassword">{t('Current Password')}</label>
          <input
            type="password"
            id="currentPassword"
            value={formData.currentPassword}
            onChange={handleInputChange}
            required
          />
        </div>
        
        <div className="password-form__field">
          <label htmlFor="newPassword">{t('New Password')}</label>
          <input
            type="password"
            id="newPassword"
            value={formData.newPassword}
            onChange={handleInputChange}
            onFocus={() => setShowRequirements(true)}
            onBlur={() => setShowRequirements(false)}
            required
          />
          {showRequirements && (
            <div className="password-form__requirements">
              <p>{t('Password must contain:')}</p>
              <ul>
                <li>{t('At least 8 characters')}</li>
                <li>{t('At least one uppercase letter')}</li>
                <li>{t('At least one number')}</li>
                <li>{t('At least one special character (#?!@$%^&*-)')}</li>
              </ul>
            </div>
          )}
        </div>
        
        <div className="password-form__field">
          <label htmlFor="confirmPassword">{t('Confirm New Password')}</label>
          <input
            type="password"
            id="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
          />
        </div>
        
        {!errors.match && (
          <div className="password-form__error">
            {t('Passwords do not match')}
          </div>
        )}
        
        {!errors.requirements && (
          <div className="password-form__error">
            {t('common.passwordValidation', 'Password must be at least 8 characters long and include at least one uppercase letter, one number, and one special character.')}
          </div>
        )}
        
        <div className="password-form__actions">
          <button 
            type="button" 
            className="password-form__button password-form__button--cancel" 
            onClick={onCancel}
          >
            {t('Cancel')}
          </button>
          <button 
            type="submit" 
            className="password-form__button password-form__button--save"
          >
            {t('Save Changes')}
          </button>
        </div>
      </form>
    </div>
  );
}

ChangePasswordForm.propTypes = {
  onChangePassword: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ChangePasswordForm;